import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ITeamStore} from "data/stores/team/team.store";
import {ITeamResponse} from "data/providers/api/team.api.provider";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import {IRound} from "data/stores/rounds/rounds.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import {includes} from "lodash";
import {getScheduledRoundStatuses} from "data/utils/helper";

interface IScoredRound extends IRound {
	pts?: number;
	isLocked?: boolean;
}

export interface ITeamScoredBarController extends ViewController {
	readonly i18n: ILocalizationStore;

	get teamScoredPoints(): Pick<ITeamResponse, "fedexPts" | "totalPts" | "roundPts">;
	get playingRound(): IRound | undefined;
	get selectedRoundIndex(): number;
	get isEditTeam(): boolean;
	get selectedTournamentRounds(): IScoredRound[];

	onSelectRound: (index: number) => void;
}

@injectable()
export class TeamScoredBarController implements ITeamScoredBarController {
	get teamScoredPoints() {
		return this._teamStore.teamScoredPoints;
	}

	get playingRound() {
		return this._tournamentsStore.playingRound;
	}

	get selectedTournamentRounds() {
		return this._tournamentsStore.selectedTournamentRounds.map((round) => {
			const roundLineup = this._teamStore.team.teamStructure.find(
				({roundId}) => roundId === round.id
			)?.lineup;
			const teamHasLineup =
				this._teamStore.hasTeam &&
				!!roundLineup?.length &&
				roundLineup.some((id) => Boolean(id));
			const isLocked = !includes(getScheduledRoundStatuses(), round.status);

			return {
				...round,
				pts: this.teamScoredPoints.roundPts.find(({roundId}) => roundId === round.id)?.pts,
				isLocked: !teamHasLineup && isLocked,
			};
		});
	}

	get selectedRoundIndex() {
		return this._tournamentsStore.selectedRoundIndex;
	}

	get isEditTeam() {
		return this._teamStore.isEditTeam;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.TeamStore) public _teamStore: ITeamStore,
		@inject(Bindings.TournamentsStore) public _tournamentsStore: ITournamentsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	onSelectRound = (index: number) => {
		if (this._teamStore.isTeamChanged) {
			this._modalsStore.showModal(ModalType.LEAVE_ROUND, {
				callback: () => {
					this._tournamentsStore.setSelectedRoundIndex(index);
					void this._teamStore.fetchTeam({
						tournamentId: this._tournamentsStore.selectedTournamentId,
					});
				},
			});
		} else {
			this._tournamentsStore.setSelectedRoundIndex(index);
		}
	};
}
